import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Loader2 } from 'lucide-react';

/**
 * BenchmarksSheet Component
 * 
 * Data Flow:
 * - Industry column (A): READ-ONLY, display only, never written by frontend
 * - Numeric columns (B, C, D): Editable, writes to Google Sheet range B4:D10
 * 
 * Google Sheet Range: B4:D10 (7 rows for 7 industries)
 * - Row 4-10: FMCG, IT Services, Auto, Logistics, Education, SAAS, Other
 * - Column B: Gross Margin %
 * - Column C: EBITDA %
 * - Column D: S&M % of Sales
 * 
 * CRITICAL: Only modifies existing cells B4:D10 - does NOT create new rows/columns
 */
const BenchmarksSheet = ({ hasAccess, onRequestAccess }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [benchmarks, setBenchmarks] = useState([
    { industry: 'FMCG', grossMargin: '', ebitda: '', smPercent: '' },
    { industry: 'IT Services', grossMargin: '', ebitda: '', smPercent: '' },
    { industry: 'Auto', grossMargin: '', ebitda: '', smPercent: '' },
    { industry: 'Logistics', grossMargin: '', ebitda: '', smPercent: '' },
    { industry: 'Education', grossMargin: '', ebitda: '', smPercent: '' },
    { industry: 'SAAS', grossMargin: '', ebitda: '', smPercent: '' },
    { industry: 'Other', grossMargin: '', ebitda: '', smPercent: '' }
  ]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/sheets/Benchmarks`);
      const data = response.data.data;
      
      // Find Industry Benchmarks header row to locate data rows
      // Expected structure: Row with "Industry" in A and "Gross Margin %" in B
      const parsedBenchmarks = [];
      let startRow = -1;
      
      for (let i = 0; i < data.length; i++) {
        if (data[i][0] === 'Industry' && data[i][1] === 'Gross Margin %') {
          startRow = i + 1;  // Data starts one row after header
          break;
        }
      }
      
      if (startRow > 0) {
        // Fetch exactly 7 industries (rows 4-10 in sheet, corresponding to B4:D10 for numeric data)
        for (let i = startRow; i < startRow + 7 && i < data.length; i++) {
          const row = data[i];
          if (row[0]) {  // Check if industry name exists in column A
            parsedBenchmarks.push({
              industry: row[0],        // Column A (read-only, display only)
              grossMargin: row[1] || '', // Column B (editable, saved to B4:D10)
              ebitda: row[2] || '',      // Column C (editable, saved to B4:D10)
              smPercent: row[3] || ''    // Column D (editable, saved to B4:D10)
            });
          }
        }
      }
      
      // Ensure we have exactly 7 industries
      if (parsedBenchmarks.length > 0) {
        if (parsedBenchmarks.length !== 7) {
          console.warn(`Expected 7 benchmark industries, found ${parsedBenchmarks.length}`);
        }
        setBenchmarks(parsedBenchmarks);
      }
    } catch (error) {
      console.error('Failed to load benchmarks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, field, value) => {
    if (!hasAccess) {
      onRequestAccess(); // trigger payment popup
      return;
    }
    const updated = [...benchmarks];
    updated[index][field] = value;
    setBenchmarks(updated);
  };

  const autoSave = async () => {
    if (!hasAccess) {
      onRequestAccess(); // Show payment dialog
      return;
    }
    setSaving(true);
    try {
      // CRITICAL: Only update cells B4:D10 (numeric values only, 7 industries)
      // This range corresponds to the 7 benchmark industries
      // Does NOT create new rows/columns - only updates existing cells
      const values = benchmarks.map(b => [
        parseFloat(b.grossMargin) || 0,
        parseFloat(b.ebitda) || 0,
        parseFloat(b.smPercent) || 0
      ]);

      // Ensure we have exactly 7 rows (one for each industry)
      if (values.length !== 7) {
        console.warn(`Expected 7 benchmark rows, got ${values.length}`);
      }

      await axios.post(`${API}/sheets/update`, {
        sheet_name: 'Benchmarks',
        range: 'B4:D10',  // EXACT range: B4 to D10 (7 rows × 3 columns)
        values: values
      });
      
      toast.success('Saved successfully');
      // Re-fetch to display updated data from B4:D10
      setTimeout(fetchData, 1000);
    } catch (error) {
      toast.error('Auto-save failed');
      console.error('Benchmark save error:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-slate-600">Loading benchmarks...</div>;
  }

  return (
    <div className="space-y-6 fade-in">
      {saving && (
        <div className="fixed top-20 right-6 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Saving...</span>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-6">Industry Benchmarks</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-200">
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Industry</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Gross Margin %</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">EBITDA %</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">S&M % of Sales</th>
              </tr>
            </thead>
            <tbody>
              {benchmarks.map((benchmark, index) => (
                <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 font-medium text-slate-700">{benchmark.industry}</td>
                  <td className="py-3 px-4">
                    <Input
                      data-testid={`benchmark-gross-${index}`}
                      type="number"
                      step="0.01"
                      value={benchmark.grossMargin}
                      onChange={(e) => handleChange(index, 'grossMargin', e.target.value)}
                      onBlur={() => autoSave()}
                      className="h-9"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <Input
                      data-testid={`benchmark-ebitda-${index}`}
                      type="number"
                      step="0.01"
                      value={benchmark.ebitda}
                      onChange={(e) => handleChange(index, 'ebitda', e.target.value)}
                      onBlur={() => autoSave()}
                      className="h-9"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <Input
                      data-testid={`benchmark-sm-${index}`}
                      type="number"
                      step="0.01"
                      value={benchmark.smPercent}
                      onChange={(e) => handleChange(index, 'smPercent', e.target.value)}
                      onBlur={() => autoSave()}
                      className="h-9"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BenchmarksSheet;
